import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Bell, 
  AlertTriangle, 
  TrendingDown,
  Clock,
  UserX,
  X,
  Settings,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLanguage } from '@/i18n/LanguageContext';

function AlertsNotification() {
  const { t, isRTL } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, high: 0, medium: 0, low: 0 });
  const [settings, setSettings] = useState({
    productivity_threshold: 60,
    compliance_threshold: 70,
    idle_time_threshold: 30,
    enabled: true
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAlerts();
    // Refresh alerts every 5 minutes
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/admin/alerts`, { withCredentials: true });
      setAlerts(response.data.alerts || []);
      setSummary(response.data.summary || { total: 0, high: 0, medium: 0, low: 0 });
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const handleDismiss = async (alertId) => {
    try {
      await axios.post(`${API}/admin/alerts/dismiss/${alertId}`, {}, { withCredentials: true });
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
      setSummary(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  const handleDismissAll = async () => {
    try {
      await axios.post(`${API}/admin/alerts/dismiss-all`, {}, { withCredentials: true });
      setAlerts([]);
      setSummary({ total: 0, high: 0, medium: 0, low: 0 });
    } catch (error) {
      console.error('Failed to dismiss all alerts:', error);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/alerts/settings`, settings, { withCredentials: true });
      setSettingsOpen(false);
      fetchAlerts();
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAlertIcon = (type) => {
    const icons = {
      low_productivity: <TrendingDown className="w-4 h-4 text-orange-500" />,
      low_compliance: <Clock className="w-4 h-4 text-red-500" />,
      long_idle: <AlertTriangle className="w-4 h-4 text-amber-500" />,
      no_checkin: <UserX className="w-4 h-4 text-blue-500" />
    };
    return icons[type] || <AlertTriangle className="w-4 h-4 text-gray-500" />;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return colors[severity] || colors.low;
  };

  const getTypeLabel = (type) => {
    const labels = {
      low_productivity: 'Low Productivity',
      low_compliance: 'Low Compliance',
      long_idle: 'Long Idle Time',
      no_checkin: 'No Check-in'
    };
    return labels[type] || type;
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="alerts-bell"
          >
            <Bell className="h-5 w-5" />
            {summary.total > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
                {summary.total > 99 ? '99+' : summary.total}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <div className="p-4 border-b bg-gradient-to-r from-gray-50 to-slate-50">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-semibold">Alerts</h4>
                <p className="text-xs text-gray-500">
                  {summary.high > 0 && <span className="text-red-600 font-medium">{summary.high} high</span>}
                  {summary.high > 0 && summary.medium > 0 && ' • '}
                  {summary.medium > 0 && <span className="text-amber-600">{summary.medium} medium</span>}
                </p>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)} title="Alert Settings">
                  <Settings className="h-4 w-4" />
                </Button>
                {alerts.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleDismissAll} className="text-xs">
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </div>

          <ScrollArea className="h-[400px]">
            {alerts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto text-indigo-400 mb-3" />
                <p className="font-medium">All Clear!</p>
                <p className="text-sm">No alerts at the moment</p>
              </div>
            ) : (
              <div className="divide-y">
                {alerts.map((alert) => (
                  <div key={alert.alert_id} className="p-3 hover:bg-gray-50 group">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{getAlertIcon(alert.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-xs ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </Badge>
                          <span className="text-xs text-gray-400">{alert.date}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">{alert.message}</p>
                        <p className="text-xs text-gray-500">{alert.department} • {getTypeLabel(alert.type)}</p>
                        {alert.value !== undefined && (
                          <div className="mt-1 flex items-center gap-2">
                            <div className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${alert.value < alert.threshold ? 'bg-red-500' : 'bg-indigo-500'}`}
                                style={{ width: `${Math.min(100, alert.value)}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{alert.value}%</span>
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDismiss(alert.alert_id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {!settings.enabled && (
            <div className="p-3 bg-amber-50 border-t border-amber-200 text-center">
              <p className="text-xs text-amber-700">Alerts are currently disabled</p>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Alert Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Alerts</Label>
                <p className="text-xs text-gray-500">Turn alerts on or off</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            {/* Productivity Threshold */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-orange-500" />
                Productivity Threshold
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.productivity_threshold}
                  onChange={(e) => setSettings(prev => ({ ...prev, productivity_threshold: parseInt(e.target.value) || 0 }))}
                  className="w-24"
                />
                <span className="text-sm text-gray-500">% (alert when below)</span>
              </div>
            </div>

            {/* Compliance Threshold */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-red-500" />
                Compliance Threshold
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.compliance_threshold}
                  onChange={(e) => setSettings(prev => ({ ...prev, compliance_threshold: parseInt(e.target.value) || 0 }))}
                  className="w-24"
                />
                <span className="text-sm text-gray-500">% (alert when below)</span>
              </div>
            </div>

            {/* Idle Time Threshold */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Idle Time Threshold
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="0"
                  value={settings.idle_time_threshold}
                  onChange={(e) => setSettings(prev => ({ ...prev, idle_time_threshold: parseInt(e.target.value) || 0 }))}
                  className="w-24"
                />
                <span className="text-sm text-gray-500">minutes (alert when exceeded)</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveSettings} disabled={loading}>
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AlertsNotification;
